import { useEffect } from 'react';
import { useStore } from './StoreContext.jsx';
import { genUid } from './uid.js';

/**
 * Generic cross-section auto-source sync.
 *
 * Watches the live store, computes a list of "auto-rows" via `computeAutoRows`,
 * and merges them into the target table at `targetPath`:
 *   - Auto-rows are matched against existing rows via `matchKeyField`
 *     (typically '_source' or '_reKey').
 *   - When an auto-row matches an existing row, user-entered fields are
 *     preserved; computed (read-only) fields are refreshed from source.
 *   - Auto-rows whose source no longer exists are removed.
 *   - Manual rows (no `matchKeyField`) pass through unchanged.
 *   - Writes only when the merged array differs from current state, so this
 *     does not loop.
 *
 * Auto-rows MUST include a `_readOnly` array listing the read-only column keys.
 *
 * The match key (`_source`/`_reKey`) is a client-only field that does NOT
 * survive a Salesforce round-trip. So after a load, a previously-auto row comes
 * back untagged and would be treated as a brand-new manual row — the auto-sync
 * would then ADD a fresh copy on top of it, duplicating every reload. Two
 * reclaim mechanisms prevent that, tried in order:
 *   1. Row `_uid` — an auto-row may carry a deterministic, household-scoped
 *      `_uid` (e.g. `${householdUid}-inc-client1`). Row uids DO survive the
 *      round-trip (sent as `uid`, restored by fromSalesforce), so an untagged
 *      existing row with the same `_uid` is the same row, no matter how its
 *      display fields (owner name, description) have changed since. This is
 *      what keeps identity stable across a spouse rename (UAT: renaming a
 *      spouse left the old income row behind and added a new one each time).
 *   2. `naturalKey` — a function returning an identity from round-tripped
 *      display fields (e.g. description). Fallback for rows saved before
 *      deterministic uids existed. Return '' to never reclaim a row.
 *
 * Pass `dropOrphans: false` for tables where the auto-row is a *starter* the
 * user fills in (e.g. Employment Income) rather than a purely-derived row. With
 * it off, an UNTAGGED row that no auto-row claims is kept (released to manual),
 * so hand-added rows always persist. A row still carrying the match-key tag
 * whose source stopped producing an auto-row is auto-managed by definition and
 * is ALWAYS removed, regardless of dropOrphans — otherwise toggling a client to
 * Retired leaves their derived income row behind and it flows to Salesforce
 * (UAT: the retirement-toggle stale-income bug). Toggling back regenerates the
 * row from the source section.
 *
 * @param {object}   args
 * @param {string}   args.targetPath       — e.g. 'liabilities.items'
 * @param {Function} args.computeAutoRows  — (data) => Array<row>
 * @param {string}   args.matchKeyField    — '_source' | '_reKey'
 * @param {Function} [args.naturalKey]     — (row) => string, for reclaiming
 * @param {boolean}  [args.dropOrphans]    — drop unclaimed auto-looking rows (default true)
 * @param {string[]} [args.seedFields]     — keys the auto row only SEEDS: applied
 *   when the row is first created, never overwritten on later merges, so the
 *   advisor can edit them — including clearing them to empty (e.g. the notes
 *   on a derived expense row)
 * @param {string[]} [args.seedWhileEmpty] — like seedFields, but an EMPTY value
 *   is re-seeded instead of preserved. For fields whose seed arrives late: the
 *   Employment Income starter row exists before the employer is typed, so the
 *   description must keep seeding until it has a value — after which the
 *   advisor owns it (a pilot advisor rewriting it to "1099 income"), and
 *   clearing it resets it to the source value on the next sync.
 */
export function useAutoSourceSync({ targetPath, computeAutoRows, matchKeyField, naturalKey, dropOrphans = true, seedFields = [], seedWhileEmpty = [] }) {
  const { data, update } = useStore();
  const targetRows = readPath(data, targetPath);

  useEffect(() => {
    const autoRows = computeAutoRows(data) || [];
    const existing = Array.isArray(targetRows) ? targetRows.filter(r => r != null) : [];

    const merged = mergeAutoRows({ existing, autoRows, matchKeyField, naturalKey, dropOrphans, seedFields, seedWhileEmpty });

    if (!arraysShallowEqual(merged, existing)) {
      update(targetPath, merged);
    }
    // We intentionally depend on `data` (full snapshot) so any source change
    // re-evaluates. update is stable. The hook itself dedupes via the equality
    // check above, so this does not loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
}

// Pure merge used by the hook (exported for testing). Given the current target
// rows and the freshly-computed auto-rows, returns the merged array:
//   - tagged auto-rows are matched by matchKeyField (live editing);
//   - after a Salesforce load the key is gone, so an untagged row is reclaimed
//     by naturalKey (read-only fields refreshed, user-entered fields kept);
//   - an untagged row that looks auto-sourced (naturalKey non-empty) but that
//     no auto-row claims is DROPPED — its source was deleted, or it's a leftover
//     duplicate from before reclaim existed. This is what stops stale/duplicate
//     "From liabilities"/"From insurance" rows from persisting and inflating
//     totals;
//   - genuinely manual rows (naturalKey empty) pass through unchanged.
export function mergeAutoRows({ existing, autoRows, matchKeyField, naturalKey, dropOrphans = true, seedFields = [], seedWhileEmpty = [] }) {
  const rows = Array.isArray(existing) ? existing.filter(r => r != null) : [];
  const auto = Array.isArray(autoRows) ? autoRows : [];
  const claimed = new Set();

  const freshAuto = auto.map(a => {
    // 1. Tagged match (live editing — the key is still present).
    let prev = rows.find(r => r?.[matchKeyField] && r[matchKeyField] === a[matchKeyField]);
    // 2. Reclaim by row uid — the post-Salesforce-load case. Deterministic
    //    auto-row uids survive the round-trip, so this match holds even when
    //    every display field (owner name, description) has since changed.
    if (!prev && a._uid) {
      prev = rows.find(r => r && !r[matchKeyField] && !claimed.has(r) && r._uid === a._uid);
    }
    // 3. Fallback: reclaim an untagged row whose natural identity matches —
    //    for rows saved before deterministic uids existed.
    if (!prev && typeof naturalKey === 'function') {
      const key = naturalKey(a);
      if (key) {
        prev = rows.find(r => r && !r[matchKeyField] && !claimed.has(r) && naturalKey(r) === key);
      }
    }
    if (prev) {
      claimed.add(prev);
      // Keep the existing row's uid: it is (or will become) the row's
      // Salesforce external identity, and swapping it mid-life would orphan
      // the saved record. The deterministic a._uid only names brand-new rows.
      const next = { ...prev, ...a, _uid: prev._uid || a._uid || genUid() };
      // Seed-only fields: the auto row provides a starting value for brand-new
      // rows but never overwrites an existing row's value — that's what makes
      // the notes on a derived row advisor-editable instead of snapping back
      // to "From assets"/"From liabilities" on the next merge.
      for (const k of seedFields) {
        if (k in prev) next[k] = prev[k];
      }
      // Seed-while-empty fields: same, except the auto row keeps feeding the
      // cell until the advisor supplies their own value.
      //
      // "The advisor supplied it" cannot be read off the cell being non-empty.
      // The source is typed one character at a time and this merge runs on
      // every keystroke, so the first letter of "Boeing" seeded a description
      // of "B" — which then looked like an advisor's edit and froze there for
      // good. So remember the value last seeded: while the cell still holds
      // exactly that, it is still ours to keep in step with the source.
      for (const k of seedWhileEmpty) {
        const seedKey = `_seed_${k}`;
        const cur = prev[k];
        const tracked = seedKey in prev;
        // A row reloaded from Salesforce carries no marker. Treat its value as
        // ours only while it is a prefix of the source, which heals the rows
        // already saved holding a single stranded letter without touching a
        // description the advisor actually wrote.
        const ours = cur === '' || cur == null
          || (tracked && cur === prev[seedKey])
          || (!tracked && typeof cur === 'string' && typeof a[k] === 'string'
              && a[k] !== '' && a[k].startsWith(cur));
        if (ours) {
          next[seedKey] = a[k];
        } else {
          next[k] = cur;
          next[seedKey] = prev[seedKey];
        }
      }
      return next;
    }
    const fresh = { ...a, _uid: a._uid || genUid() };
    for (const k of seedWhileEmpty) fresh[`_seed_${k}`] = a[k];
    return fresh;
  });

  // What passes through alongside the fresh auto-rows. A row still carrying
  // the match-key tag but that no auto-row claimed is auto-managed and its
  // source just vanished (e.g. the client toggled to Retired) — it is ALWAYS
  // dropped so a phantom row can't linger on screen or flow to Salesforce.
  // For untagged unclaimed rows:
  //   - dropOrphans (default): only genuinely manual rows survive. One that
  //     looks auto-sourced (naturalKey non-empty) is a deleted-source orphan
  //     and is dropped — this stops stale/duplicate "From liabilities"/"From
  //     insurance" rows from inflating totals.
  //   - dropOrphans false: every untagged unclaimed row is kept (released to
  //     manual), so hand-added rows (Employment/Other Income) never disappear.
  const isOrphanAuto = r => typeof naturalKey === 'function' && !!naturalKey(r);
  const passthrough = dropOrphans
    ? rows.filter(r => r && !r[matchKeyField] && !claimed.has(r) && !isOrphanAuto(r))
    : rows.filter(r => r && !r[matchKeyField] && !claimed.has(r));

  return [...freshAuto, ...passthrough];
}

function readPath(obj, path) {
  if (!obj) return undefined;
  return path.split('.').reduce((cur, k) => (cur == null ? cur : cur[k]), obj);
}

function arraysShallowEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (!objectShallowEqual(a[i], b[i])) return false;
  }
  return true;
}

function objectShallowEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, k)) return false;
    const av = a[k];
    const bv = b[k];
    if (av === bv) continue;
    if (Array.isArray(av) && Array.isArray(bv)) {
      if (av.length !== bv.length) return false;
      for (let i = 0; i < av.length; i++) {
        if (av[i] !== bv[i]) return false;
      }
      continue;
    }
    return false;
  }
  return true;
}
